import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { recallListSearch, rememberListSearch } from '../shared/listSearchMemory';

/**
 * Keeps a list's search in step with the page-load memory of its history entry
 * (see `listSearchMemory`) so browser back/forward can bring it back.
 *
 * Lists read the memory in their `useState` initializers for a fresh mount, but
 * React Router keeps the same list mounted when navigation only changes the
 * history entry (an active sidebar click pushes a new entry for the same route;
 * Back then pops to the old one). On such a POP this hook restores the search
 * remembered for the entry through `restoreSearch` before anything is written,
 * so the older memory is not overwritten with the current search. PUSH/REPLACE
 * entries are new and simply record the current search, which preserves the
 * explicit empty-search reset an active sidebar click hands over.
 */
export function useRememberListSearch(
    searchQuery: string,
    restoreSearch: (search: string) => void,
): void {
    const { key } = useLocation();
    const navigationType = useNavigationType();
    const activeKey = useRef(key);
    const restoreRef = useRef(restoreSearch);
    restoreRef.current = restoreSearch;

    useEffect(() => {
        if (activeKey.current !== key) {
            activeKey.current = key;
            if (navigationType === 'POP') {
                const remembered = recallListSearch(key) ?? '';
                if (remembered !== searchQuery) {
                    // Write once the state reflects the restore (next run).
                    restoreRef.current(remembered);
                    return;
                }
            }
        }
        rememberListSearch(key, searchQuery);
    }, [key, navigationType, searchQuery]);
}
